import { isSameDay, isSameWeek, isSameYear } from 'date-fns'
import { type IETFLanguageTag } from '@opallabs/concepts'
import { ensureValidDate } from '../validateDate'
import { ensureValidLocale } from '../validateLocale'

type Options = Readonly<{
  locale?: IETFLanguageTag
}>

/**
 * Display a timestamp based on how far it is from the current instant in time.
 * Unlike most implementations this doesn’t describe the distance (for example,
 * ‘15 minutes ago’); instead it determines how much information to show (for
 * example: ‘Now’, ‘3:07 PM’, ‘Tuesday’, ‘Jun 24’, ‘Jun 24, 2023’).
 *
 * @todo Add a `startOfWeek?: WeekStartsOn` option to pass to `isSameWeek` once
 * `Intl.Locale.getWeekInfo()` has robust cross-browser support.
 * @note For similar behavior that only deals in whole days (no ‘Now’ or time), see `relativeDate`.
 */
export function relativeTimestamp(
  date: Date,
  { locale = navigator.language }: Options = {}
) {
  ensureValidDate(date)
  ensureValidLocale(locale)

  const now = new Date()
  const timeAgoInMinutes = (now.valueOf() - date.valueOf()) / 1000 / 60

  // Note: unlike all other output from this function `"Now"` is not yet
  // localized.
  if (timeAgoInMinutes >= 0 && timeAgoInMinutes <= 1) return 'Now'

  const options: Intl.DateTimeFormatOptions = {}

  // Time is only meaningful on the current day; past that it's more precision
  // than actually matters.
  //
  // Note: These checks intentionally use `isSameDay` and `isSameYear` rather
  // than checking if `date` is ‘within a day/year’, so that output like
  // `"12:00 PM"` or `"Dec 30"` can only have one plausible meaning.
  if (isSameDay(now, date)) {
    options.hour = 'numeric'
    options.minute = 'numeric'
  } else {
    if (isSameWeek(now, date)) {
      options.weekday = 'long'
    } else {
      options.day = 'numeric'
      options.month = 'long'

      if (!isSameYear(now, date)) {
        options.year = 'numeric'
      }
    }
  }

  return date.toLocaleString(locale, options)
}
